/**
 * NAZWY KODÓW OPERACJI SPS — jedno miejsce dla Transakcji i dla ekranu uprawnień.
 *
 * ⚠️ SPS NIE TRZYMA ZNACZEŃ SWOICH KODÓW W BAZIE. Tabela `Transactions` ma DOKŁADNIE JEDNĄ
 * kolumnę — sam kod, bez opisu (49 wierszy u METPOL-u i u DBW). Nazwy widoczne w oknie „Operacje"
 * są zaszyte w aplikacji SPS, a nie w danych klienta, więc portal nie ma ich skąd wyczytać.
 *
 * ⚠️⚠️ SKĄD WIĘC TE NAZWY: ROZPRACOWANE DOŚWIADCZALNIE 14.08.2026, nie zgadnięte. Na DBW powstała
 * pusta grupa `TESTOWA_1`; użytkownik dodawał uprawnienia pojedynczo, a obserwator co trzy sekundy
 * notował, który kod pojawia się w `UserGroupTransactions`. Pierwsza dziesiątka dała kolejność
 * i ujawniła, że lista „[Wybrane]" w SPS jest **sortowana kodem, nie nazwą** — dzięki temu resztę
 * dało się przyłożyć pozycja po pozycji do posortowanej listy kodów z bazy.
 *
 * Sprawdzianem było te dziesięć potwierdzonych pojedynczo: wszystkie wypadły na swoich pozycjach,
 * łącznie z `SU` i `UU` na 35–36 miejscu, czyli daleko od punktu zaczepienia. Osobno potwierdzone
 * `RSI` („Return Serialized Narzedzie") przesądziło kolejność cyfr przed literami, a więc
 * i pozycje `RS1`/`RS2`.
 *
 * ⚠️ NADAL NIE ROZWIJAMY SKRÓTÓW NA WYCZUCIE. `RFI` i `WFI` **nie mają nazwy w samym SPS** —
 * okno pokazuje surowe klucze `TRAN_RFI` i `TRAN_WFI`, bo brakuje im tłumaczenia. Zostają więc
 * bez nazwy także u nas: wymyślona nazwa zamieniłaby domysł w fakt widoczny dla operatora, który
 * na tej podstawie nadaje komuś prawo. Kod bez nazwy jest uczciwszy niż nazwa wymyślona.
 *
 * ⚠️ NAZWY SĄ POPRAWIONE WZGLĘDEM SPS — świadomie, na prośbę użytkownika („to kuje w oczy").
 * SPS ma tu „Zwrot z regeneracjido nowych" bez spacji, „Uzupełnianie ogólne artykułu" i pół
 * przetłumaczone „Reserve Narzedzie". Zachowujemy ZNACZENIE i kolejność członów, żeby dało się
 * skojarzyć z ekranem SPS przez telefon, ale zapis jest nasz. Kod obok nazwy zostaje zawsze —
 * to on jest wspólnym językiem z SPS-em.
 */
/**
 * ⚠️⚠️ DRUGIE ŹRÓDŁO (2026-08-14): dokument dostawcy „Opisy operacji — Raporty", 30 kodów
 * w tabeli `Transaction | Code | Description`. Pierwszy papier, jaki mamy na te znaczenia —
 * dotąd wszystko pochodziło z obserwacji bazy. Potwierdził nasze nazwy z jednym wyjątkiem
 * i przyniósł jedną poprawkę.
 *
 * ⚠️⚠️ TEN DOKUMENT PRZECZY SAM SOBIE PRZY `SU` I `UU` — i to jest najważniejsza rzecz do
 * zapamiętania, bo dotyczy tego, KTO cofnął cudze wydanie:
 *
 *   SU · polska kolumna: „Anulowanie przez użytkownika"
 *        angielska:      „Given to ADMINISTRATORS to reverse transactions … by OTHER USERS"
 *   UU · polska kolumna: „Anulowanie przez administratora"
 *        angielska:      „Allows A USER to reverse a transaction mistakenly carried out"
 *
 * Polskie podpisy są w tym dokumencie ZAMIENIONE MIEJSCAMI względem jego własnego opisu.
 * Trzymamy się angielskiego, bo zgadza się i ze skrótami (`SU` = supervisor undo, `UU` = user
 * undo), i z naszym wcześniejszym rozpoznaniem. Nie zmieniać na „zgodne z papierem" bez
 * sprawdzenia tego akapitu — zamiana przypisałaby korektę magazyniera administratorowi.
 *
 * ⚠️ POPRAWIONY KIERUNEK PRZY `WR`/`WRD`/`WS`/`WSD`. Mieliśmy „Pobranie DO regeneracji", czyli
 * cel; dokument mówi wprost, że to wydanie rzeczy, które JUŻ SĄ regeneracją albo złomem, z bramy
 * albo z szuflady („Withdraw items with a content type of Rework from a bin — referred to as
 * Pickup Rework from Bin"). To odwrotny kierunek: nie oddajemy czegoś do regeneracji, tylko
 * zabieramy regeneraty. Stąd „Pobranie regeneracji" i „Pobranie złomu".
 *
 * ⚠️ ZAPIS ZOSTAJE NASZ (decyzja użytkownika 2026-08-14): bierzemy z dokumentu ZNACZENIE, nie
 * brzmienie. Dostawca ma tam „Przenieś magazyn" i „Uzupełnienie ogólnego artykułu"; nasze
 * „Przesunięcie magazynowe" i „Uzupełnienie — artykuł ogólny" czytają się lepiej i trzymają
 * rodzinę operacji razem po posortowaniu listy.
 */

/**
 * Kody z POTWIERDZONYM znaczeniem — tłumaczenia leżą pod `ops.code.<KOD>`.
 *
 * ⚠️ `DD` DOSZŁO 2026-08-14 I PRZYSZŁO INNĄ DROGĄ NIŻ RESZTA. Okno uprawnień SPS go nie oferuje
 * (49 kodów w bazie, 48 pozycji na liście), więc doświadczeniem na grupie nie dało się go
 * sprawdzić — i dlatego siedział tu wcześniej jako goły kod. Znaczenie dał dopiero dokument
 * dostawcy: „Occurs when an item is unassigned from a drawer", czyli ODPIĘCIE TOWARU OD SZUFLADY,
 * a nie skasowanie samej szuflady (polski podpis w tym dokumencie jest skrótem myślowym).
 * Nadać go nadal nie sposób, ale w historii wydań potrafi się pojawić — i teraz ma nazwę.
 *
 * ⚠️ `RFI` i `WFI` nadal nie — patrz nagłówek. Dokument dostawcy ich nie obejmuje.
 */
export const KODY_POTWIERDZONE = [
  'AML', 'CC', 'CPV', 'CPW', 'DBS', 'DD', 'FIW', 'FPR', 'IA', 'IST', 'LR',
  'MOV', 'OML', 'R_R', 'REG', 'REN', 'REU', 'REX', 'RG', 'RK', 'RN',
  'RR', 'RRG', 'RRN', 'RRS', 'RRU', 'RS', 'RS1', 'RS2', 'RSI', 'RU',
  'SCL', 'SCS', 'STD', 'SU', 'UU', 'WEX', 'WG', 'WI', 'WKT', 'WN',
  'WP', 'WR', 'WRD', 'WS', 'WSD', 'WU',
] as const;

/**
 * NAZWA UPRAWNIENIA — pełna, opisowa, na ekran nadawania praw.
 *
 * ⚠️ OSOBNA OD NAZWY TYPU TRANSAKCJI, i to jest sedno rozdziału tych dwóch funkcji. W historii
 * wydań kolumna „Typ" ma mieć jedno słowo („wydanie"), a na ekranie uprawnień ten sam kod musi
 * powiedzieć, CZEGO dotyczy prawo („Pobranie — artykuł nowy"). Jedna wspólna nazwa znaczyłaby
 * albo rozwlekłą kolumnę w tabeli wydań, albo uprawnienie opisane słowem „wydanie", z którego
 * nie wynika nic.
 */
export function nazwaOperacji(t: (k: string) => string, code: string): string {
  const nazwa = t(`ops.code.${code}`);
  return nazwa === `ops.code.${code}` ? code : nazwa;
}

/**
 * NAZWA TYPU TRANSAKCJI — krótka, do kolumny w historii wydań.
 *
 * ⚠️ `t()` oddaje KLUCZ, gdy brak tłumaczenia — bez tego porównania na ekranie pojawiłoby się
 * „tx.code.MOV" zamiast „MOV".
 *
 * ⚠️ SPADA NA NAZWĘ UPRAWNIENIA, gdy krótkiej nie ma. Krótkie mamy dla trzech kodów, które realnie
 * zapełniają historię wydań; dla reszty lepiej pokazać pełną nazwę operacji niż goły kod.
 */
export function nazwaKodu(t: (k: string) => string, code: string): string {
  const krotka = t(`tx.code.${code}`);
  if (krotka !== `tx.code.${code}`) return krotka;
  return nazwaOperacji(t, code);
}

/**
 * CZTERY PODSTAWOWE OPERACJE — RESZTA POD SPODEM.
 *
 * ════════════════════════════════════════════════
 *  ⚠️ POWSTAŁO ZE ZGŁOSZENIA Z UŻYTKOWANIA (2026-08-25): „mamy listę ponad 40 uprawnień, gdzie
 *  prawie żadne nie jest używane i trzeba scrollować po te podstawowe". Słownik wyspy ma 47 kodów,
 *  a na testówce PESA nadane są DWA: `WN` (12 grup) i `REN`/`IA` (po 2 osoby).
 *
 *  ⚠️⚠️ STOI TU, A NIE W OKNIE, OD 04.09.2026 — bo od tego dnia dzielą tę listę DWA ekrany:
 *  okno edycji uprawnień i wybór przy zakładaniu grupy („żeby było dokładnie jak w edycji
 *  uprawnień"). Dwie kopie tej czwórki rozjechałyby się przy pierwszej zmianie, a różnica byłaby
 *  cicha: jeden ekran pokazywałby na górze co innego niż drugi.
 * ════════════════════════════════════════════════
 *
 * ⚠️ KOLEJNOŚĆ JEST PODANA PRZEZ UŻYTKOWNIKA I NIE JEST ALFABETYCZNA: wydawanie → uzupełnianie →
 * korekta stanów → zwrot. To kolejność, w jakiej się o tym myśli przy nadawaniu uprawnień,
 * i dlatego nie sortujemy tej czwórki razem z resztą.
 *
 * ⚠️ RODZINA „NOWE", NIE „OGÓLNE", i to wynik pomiaru na danych klienta, nie domysł: nadane jest
 * `WN`/`REN`, a nie `WG`/`REG` — u tego klienta towar jest „nowy".
 */
export const PODSTAWOWE_OPERACJE: readonly string[] = ['WN', 'REN', 'IA', 'RN'];

/**
 * `WN` — „Pobranie — artykuł nowy"; bliźniak stałej `KOD_WYDAJ_NOWY_ARTYKUL` z API.
 *
 * ⚠️ ZAZNACZANY Z GÓRY W NOWEJ GRUPIE (zamówione 04.09.2026: „pobranie nowego artykułu możesz
 * domyślnie zaznaczyć przy tworzeniu nowej grupy"). Ten sam kod nadaje integracja przy zakładaniu
 * grupy przez `/api/v1` — bo grupa bez niego nie pozwala pobrać niczego, czyli nie robi tego,
 * po co się ją zakłada.
 *
 * ⚠️⚠️ ALE TYLKO GDY SŁOWNIK WYSPY GO ZNA — patrz `WyborOperacjiGrupy`. API ODMAWIA założenia
 * grupy z kodem, którego nie ma w tabeli `Transactions` („Ta instalacja SPS nie zna kodów
 * operacji"), więc domyślny ptaszek wstawiony na słepo wywalałby ZAKŁADANIE GRUPY na instalacji
 * bez `WN` — i to jest gorsze od braku domyślności.
 */
export const KOD_POBRANIE_NOWY = 'WN';

/**
 * Podział listy kodów na podstawowe (w kolejności `PODSTAWOWE_OPERACJE`) i pozostałe.
 *
 * ⚠️ PODZIAŁ LICZONY Z TEGO, CO WYSPA ZNA, nie z naszej listy — inaczej brak `RN` w słowniku
 * dawałby puste miejsce w sekcji podstawowej.
 *
 * ⚠️ RESZTA ZOSTAJE W KOLEJNOŚCI WEJŚCIOWEJ: to wołający decyduje, czy sortuje po nazwie
 * (oba dzisiejsze ekrany sortują), i tylko on wie, czym są jego pozycje.
 */
export function podzielOperacje<T>(kody: readonly T[], kod: (k: T) => string): { podstawowe: T[]; dodatkowe: T[] } {
  const podstawowe = PODSTAWOWE_OPERACJE.map((c) => kody.find((k) => kod(k) === c)).filter(
    (k): k is T => k !== undefined,
  );
  return { podstawowe, dodatkowe: kody.filter((k) => !PODSTAWOWE_OPERACJE.includes(kod(k))) };
}

/** Czy dla tego kodu mamy potwierdzoną nazwę — do wyróżnienia na liście. */
export const kodZnany = (code: string): boolean => (KODY_POTWIERDZONE as readonly string[]).includes(code);

/**
 * CO DANA OPERACJA ROBI — 30 opisów z dokumentu dostawcy, przełożonych na polski.
 *
 * ⚠️ NIGDZIE TEGO JESZCZE NIE POKAZUJEMY i to jest decyzja użytkownika z 2026-08-14, a nie
 * niedokończona robota. Leży tu, bo była to jedyna kopia tej wiedzy poza plikiem `.docx` na
 * czyimś pulpicie — a nazwa operacji mówi CO to jest, nie KIEDY tego użyć. Gdy przyjdzie
 * miejsce w interfejsie (dymek na liście uprawnień albo w raportach), tekst jest gotowy.
 *
 * ⚠️ NIE JEST TO TŁUMACZENIE SŁOWO W SŁOWO. Zdania dostawcy opisują ekrany SPS-a
 * („referred to as Pickup Rework from Bin in the User Interface"); zostawiamy z nich to, co
 * mówi o SKUTKU operacji. Gdzie polska kolumna dokumentu kłóci się z angielską — czyli przy
 * `SU` i `UU` — trzymamy się angielskiej, patrz nagłówek pliku.
 *
 * ⚠️ CELOWO NIE W `pl.json`: to nie jest napis interfejsu, tylko wiedza o dziedzinie, i dopóki
 * nic jej nie wyświetla, w pliku tłumaczeń byłaby martwym kluczem, który ktoś kiedyś „posprząta".
 */
export const OPISY_OPERACJI: Readonly<Record<string, string>> = {
  CPV: 'Zmiana uprawnień do operacji bez wchodzenia w ustawienia automatu.',
  CPW: 'Zmiana hasła pracownika bez wchodzenia w ustawienia automatu.',
  DD: 'Towar zostaje odpięty od szuflady — miejsce przestaje być do niego przypisane.',
  FPR: 'Zapisanie odcisku palca pracownika na potrzeby logowania.',
  IA: 'Inwentaryzacja pojedynczych miejsc; zapisuje różnicę (+/−), a nie stan końcowy.',
  MOV: 'Przeniesienie towaru między miejscami W TYM SAMYM automacie.',
  REG: 'Uzupełnienie towaru w miejscu o zawartości „ogólnej".',
  REN: 'Uzupełnienie towaru w miejscu o zawartości „nowe".',
  REU: 'Uzupełnienie towaru w miejscu o zawartości „używane".',
  RG: 'Zwrot towaru do szuflady o zawartości „ogólnej" — tylko dla rzeczy wielokrotnego użytku.',
  RK: 'Zwrot klucza po użyciu.',
  RN: 'Zwrot towaru do szuflady o zawartości „nowe" — tylko dla rzeczy wielokrotnego użytku.',
  RR: 'Zwrot towaru do miejsca o zawartości „regeneracja".',
  RRG: 'Zwrot z regeneracji do szuflady o zawartości „ogólnej".',
  RRN: 'Zwrot z regeneracji do szuflady o zawartości „nowe".',
  RRS: 'Zwrot z regeneracji do szuflady o zawartości „złom".',
  RRU: 'Zwrot z regeneracji do szuflady o zawartości „używane".',
  RS: 'Zwrot towaru do miejsca o zawartości „złom".',
  RU: 'Zwrot towaru do szuflady o zawartości „używane" — tylko dla rzeczy wielokrotnego użytku.',
  SU: 'Cofnięcie wydania POMYŁKOWO wykonanego PRZEZ INNEGO pracownika — prawo dla administratora.',
  UU: 'Cofnięcie WŁASNEGO, pomyłkowo wykonanego wydania.',
  WG: 'Pobranie towaru o zawartości „ogólnej".',
  WKT: 'Pobranie całego zestawu towarów.',
  WN: 'Pobranie towaru o zawartości „nowe".',
  WP: 'Pobranie z pominięciem WSZYSTKICH kroków przed listą towarów (zlecenie, automat itd.).',
  WR: 'Zabranie ze SKRZYNI rzeczy oznaczonych jako regeneracja.',
  WRD: 'Zabranie z SZUFLADY rzeczy oznaczonych jako regeneracja.',
  WS: 'Zabranie ze SKRZYNI rzeczy oznaczonych jako złom.',
  WSD: 'Zabranie z SZUFLADY rzeczy oznaczonych jako złom.',
  WU: 'Pobranie towaru o zawartości „używane".',
};
